import { DRAGON, GHOST_POWER_BP, INCURSION, SAGA, SKILLS, type SagaBattle, type SagaChapter } from '@somnhold/content';
import { DEFAULT_PROFILE, skillLevelBp, type BattleParams } from '../battle/create.ts';
import { worldToHex } from '../hex.ts';
import { createClearingMap } from '../map.ts';
import { cosA, idiv, imax, imin, mulBp, sinA } from '../math/fixed.ts';
import { rngInt, seedRng } from '../math/rng.ts';
import type { BattleRules, BattleState } from '../types.ts';
import { bigParty, crowd } from './party.ts';
import { event, findPlayer, heroName } from './core.ts';
import { promiseUnique } from './items.ts';
import { fightKit, grantXp } from './progress.ts';
import type { CampaignState, Ms, SagaState } from './types.ts';

/**
 * The dragon of the Burn's fight (docs/UNICAS.md §4): its own clearing, the old harpoons on its rim, the absent
 * friends' spectres, the scales the group's tries left broken; and what each try leaves for the next (its scars, the
 * hint after a loss, the armour given anyway).
 */

const D = DRAGON;
/** A full turn of the dragon's circle, in its steps (battle/dragon.ts). */
const TURN = 8192;

/** Whether an act is the dragon's fight. */
export const isDragonAct = (act: { kind: string; mode?: string; boss?: { kind: string } } | undefined): boolean => act?.kind === 'battle' && act.mode === 'boss' && act.boss?.kind === 'dragon';

/** The dragon's act index in its chapter (-1: none). */
export const dragonActOf = (ch: SagaChapter): number => ch.acts.findIndex((a) => isDragonAct(a));

/** A table by heroes (1, 2, 3…) read at a count in bp, between its whole numbers; past its end, its last. */
const lerp = (table: readonly number[], nBp: number): number => {
  const lo = imax(1, idiv(nBp, 10000));
  const a = table[imin(lo, table.length - 1)] as number;
  const b = table[imin(lo + 1, table.length - 1)] as number;
  return a + idiv((b - a) * (nBp - lo * 10000), 10000);
};

/** Its life by heroes counted (bp): the act's table up to four, BIG_PARTY's boss rule past it, read in between. */
const lifeAt = (hp: readonly number[], nBp: number): number => {
  const lo = imax(1, idiv(nBp, 10000));
  const a = bigParty(hp, lo, SAGA.bigBoss);
  const b = bigParty(hp, lo + 1, SAGA.bigBoss);
  return a + idiv((b - a) * (nBp - lo * 10000), 10000);
};

/** Average level ×100 of some heroes (0 for none). */
const avg100 = (s: CampaignState, pids: string[]): number => {
  if (!pids.length) return 0;
  let lv = 0;
  for (const pid of pids) lv += findPlayer(s, pid)?.hero.level ?? 1;
  return idiv(lv * 100, pids.length);
};

/**
 * The fight's scale (§4.11): the heroes counted (live ones whole, each spectre `spectreBp`; the cast never), and the
 * level everything is measured at, the live heroes' average or the spectres' if higher (never below the live ones').
 */
export const dragonScale = (s: CampaignState, live: string[], ghosts: string[]): { nBp: number; avg: number; levelBp: number } => {
  const nBp = imax(10000, live.length * 10000 + ghosts.length * D.spectreBp);
  const a = imax(avg100(s, live), avg100(s, ghosts)) || 100;
  return { nBp, avg: imax(1, idiv(a, 100)), levelBp: 10000 + idiv((a - 100) * SAGA.perLevelBp, 100) };
};

/** The Frost Lance's blow at a level: what the glass's cold is measured in. */
export const refLance = (level: number): number => mulBp(SKILLS.frostLance.damage, skillLevelBp(level));

/** Spectres who can come along: the campaign's other heroes, not playing live, up to eight in all. */
export const dragonGhosts = (s: CampaignState, live: string[], ghosts: string[]): string[] =>
  [...new Set(ghosts)].filter((g) => !live.includes(g) && !!findPlayer(s, g) && !findPlayer(s, g)?.left).slice(0, imax(0, 8 - live.length));

/**
 * The dragon's clearing for a party: live heroes and spectres, the cast the group earned (Gaspar, Maelis, the
 * village's turrets) and La Quemadura's edges (§4.6), and what its tries left on it (the scars, §4.7).
 */
export const dragonParams = (s: CampaignState, act: SagaBattle, live: string[], ghosts0: string[], seed: number, tough: number, mem: { aff: Partial<Record<string, number>>; flags: string[] }): BattleParams => {
  const ghosts = dragonGhosts(s, live, ghosts0);
  const k = dragonScale(s, live, ghosts);
  const rng = seedRng(seed ^ 0xd7a9);
  const R = D.field.radius;
  const map = createClearingMap(R);
  const done = s.sides?.burn?.done ?? [];
  // The old harpoons, evenly round the rim (a fourth one, rebuilt by Hilde, with La Quemadura 2 done).
  const n = D.harpoons.n + (done[1] ? 1 : 0);
  const off = rngInt(rng, TURN);
  const harpoons: NonNullable<BattleRules['dragon']>['harpoons'] = [];
  const posts: { x: number; y: number }[] = [{ x: 0, y: 0 }];
  for (let i = 0; i < n; i++) {
    const arc = (off + idiv(i * TURN, n)) % TURN;
    const a = arc >> 3;
    const c = cosA(a);
    const sn = sinA(a);
    const at = (r: number, side: number): { x: number; y: number } => ({ x: idiv(c * r - sn * side, 10000), y: idiv(sn * r + c * side, 10000) });
    const gun = at(D.harpoons.ring, 0);
    // Its lever beside it, on the side the dragon comes from (whoever pulls it sees it coming).
    const lever = at(D.harpoons.ring - 400, -D.harpoons.leverOff);
    harpoons.push({ x: gun.x, y: gun.y, lx: lever.x, ly: lever.y, arc });
    posts.push(at(D.harpoons.ring - 1800, 0));
  }
  const level = k.avg;
  const lance = refLance(level);
  const scars = imin(D.scars.max, s.saga?.dragon?.scars.length ?? 0);
  const cracked = done[2] ? 1 : 0;
  const big = crowd(SAGA.partyBp, idiv(k.nBp, 10000), SAGA.big);
  const rules: BattleRules = {
    turrets: { ...INCURSION.turrets },
    bossWins: true,
    twist: 'burnClearing',
    enemyHpBp: mulBp(mulBp(SAGA.enemyHpBp, k.levelBp), big.hpBp),
    dragon: {
      radius: R,
      harpoons,
      posts,
      ask: imax(1, mulBp(lerp(D.scales.lances, k.nBp), lance)),
      close: imax(1, mulBp(D.scales.closeBp, lance)),
      fixed: cracked + scars,
      cracked,
      spectres: ghosts,
      heroesBp: k.nBp,
      ...(done[0] ? { warnBp: D.burn.warnBp } : {}),
      ...(done[3] ? { beaconR: D.burn.beaconR } : {}),
      ...(s.saga?.dragon?.fire ? { fireWarn: true } : {}),
    },
  };
  // The village's carts of bolts, if Odna trusts the garrison this much: more turrets for every hero.
  const a = D.allies;
  if ((mem.aff.odna ?? 0) >= a.villageAff && rules.turrets) rules.turrets = { ...rules.turrets, max: rules.turrets.max + a.villageTurrets };
  const heroes: BattleParams['heroes'] = [];
  // The live heroes start in the middle; the spectres by the harpoons first, so one stands near each.
  live.forEach((pid) => {
    const p = findPlayer(s, pid);
    if (p) heroes.push({ pid, name: heroName(p), cls: p.hero.heroClass, level: p.hero.level, control: 'live', powerBp: 10000, post: 0, relics: p.hero.relics, awakened: p.hero.awakened ?? [], ...fightKit(p.hero, 'saga', s) });
  });
  ghosts.forEach((pid, i) => {
    const c = findPlayer(s, pid);
    if (c) heroes.push({ pid, name: heroName(c), cls: c.hero.heroClass, level: c.hero.level, control: 'ghost', powerBp: GHOST_POWER_BP, post: 1 + (i % posts.length), profile: c.ghostProfile, relics: c.hero.relics, awakened: c.hero.awakened ?? [], ...fightKit(c.hero, 'saga', s) });
  });
  // The cast (never counted, never eaten): Ser Gaspar's spectre, Maelis, Orla with La Quemadura done.
  const npc = (pid: string, name: string, cls: 'paladin' | 'cleric' | 'ranger', post: number): BattleParams['heroes'][number] => ({ pid, name, cls, level, control: 'ghost', powerBp: a.powerBp, post, profile: DEFAULT_PROFILE(cls) });
  if (mem.flags.includes(a.gaspar)) heroes.push(npc('npc:gaspar', 'Ser Gaspar', 'paladin', 0));
  if (mem.flags.includes(a.maelis)) heroes.push(npc('npc:maelis', 'Maelis', 'cleric', 0));
  if (done[3]) heroes.push(npc('npc:orla', 'Orla', 'ranger', 1));
  // On its circle, past the first harpoon: it comes round to them.
  const start = (off + idiv(TURN, n * 2)) >> 3;
  const hp = mulBp(mulBp(mulBp(lifeAt(act.boss?.hp ?? [0, 1], k.nBp), k.levelBp), tough), D.hpBp);
  return {
    mode: 'incursion',
    seed,
    map,
    structures: [],
    heroes,
    keepHp: 1,
    keepMaxHp: 1,
    marea: s.mareaIndex,
    wave: 0,
    spawns: [{ tick: 24, sector: 0, kind: 'dragon', tier: 3, elite: false, hp, boss: true, at: worldToHex(idiv(cosA(start) * D.orbit, 10000), idiv(sinA(start) * D.orbit, 10000)) }],
    rules,
  };
};

/* ---------- what a try leaves ---------- */

/** The line the way back adds after a lost try (§4.5): the worst mistake, by fixed rules. */
export type DragonHint = 'fire' | 'scales' | 'harpoons' | 'eaten' | 'glass';

/** The worst mistake of a lost try (null: none stands out). */
export const dragonHint = (b: BattleState): DragonHint | null => {
  const st = b.dragon;
  const r = b.rules?.dragon;
  if (!st || !r) return null;
  if (st.closed >= 2 || st.fireAll >= r.close * 3) return 'fire';
  if (st.broke === 0) return 'scales';
  if (st.downs === 0) return 'harpoons';
  if (st.eaten.length >= 2) return 'eaten';
  if (st.lost === 'glass') return 'glass';
  return null;
};

/** The armour of the hero's class, now or once they reach the level it asks (§4.12). */
const armour = (s: CampaignState, pid: string, ch: SagaChapter, now: Ms): void => {
  const p = findPlayer(s, pid);
  if (p) promiseUnique(s, p, ch.unique ?? 'armor', ch.uniqueLevel ?? 20, now);
};

/**
 * A try at the dragon in the chapter being played, applied: its scar if its life went under 30 % (two at most, for
 * every try after, by anyone), the hint and the fire warning if lost, and after the armour given anyway, the armour for
 * a newcomer who puts it under 30 % too. The spectres' owners get a share of a won act's XP (never "played").
 */
export const dragonTry = (s: CampaignState, m: SagaState, ch: SagaChapter, who: string[], b: BattleState, now: Ms): void => {
  const st = b.dragon;
  const dg = (m.dragon ??= { scars: [], rounds: 0, armed: false, low: false, played: [] });
  for (const pid of who) if (!dg.played.includes(pid)) dg.played.push(pid);
  if (st?.low) {
    dg.low = true;
    if (dg.scars.length < D.scars.max) {
      dg.scars.push({ pid: who[0] ?? '', at: now });
      event(s, now, 'saga', { pid: who[0] ?? '', data: { kind: 'scar', chapter: m.chapter, n: dg.scars.length } });
    }
    if (dg.planB) for (const pid of who) armour(s, pid, ch, now);
  }
  if (b.result === 'won') {
    delete dg.hint;
    delete dg.fire;
    const share = mulBp(SAGA.actXp, INCURSION.carryShareBp);
    for (const g of b.rules?.dragon?.spectres ?? []) {
      if (who.includes(g) || !findPlayer(s, g)) continue;
      grantXp(s, g, share, now);
      event(s, now, 'carried', { pid: g, data: { by: who[0] ?? '', xp: share } });
    }
    return;
  }
  const hint = dragonHint(b);
  if (hint) dg.hint = hint;
  else delete dg.hint;
  if (hint === 'fire') dg.fire = true;
};

/**
 * A round of the chapter lost at the dragon (out of provisions): it starts again from the dragon. Past its two scars,
 * a round with a try that put it under 30 % counts toward the armour given anyway (§4.12).
 */
export const dragonRoundLost = (s: CampaignState, m: SagaState, ch: SagaChapter, now: Ms): void => {
  const dg = (m.dragon ??= { scars: [], rounds: 0, armed: false, low: false, played: [] });
  if (dg.armed && dg.low) dg.rounds++;
  if (!dg.planB && dg.rounds >= D.planB.rounds) {
    dg.planB = true;
    for (const pid of [...dg.played].sort()) armour(s, pid, ch, now);
    event(s, now, 'saga', { data: { kind: 'planB', chapter: m.chapter, played: [...dg.played].sort().join(',') } });
  }
  dg.armed = dg.scars.length >= D.scars.max;
  dg.low = false;
};
